import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import bezier from 'bezier-easing';
import { WheelService } from '../wheel.service';
import { CountdownPipe } from '@old-app/pipes/countdown.pipe';
import { animateElement } from '@core/utils/deprecated.utils';
import { AsyncPipe } from '@angular/common';
import { WheelColor } from '../interfaces/wheel.interface';

@Component({
  selector: 'app-wheel-main',
  templateUrl: './wheel-main.component.html',
  styleUrls: ['./wheel-main.component.scss'],
  standalone: true,
  imports: [CountdownPipe, AsyncPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WheelMainComponent {
  public readonly wheelService = inject(WheelService);
  private readonly destroyRef = inject(DestroyRef);
  wheelRef = viewChild.required<ElementRef<HTMLDivElement>>('wheel');

  currentColor = signal<WheelColor>('gold');
  arrowColorBorder: Record<WheelColor, string> = {
    gold: 'white',
    gray: 'var(--wheel-gray)',
    blue: 'var(--wheel-blue)',
    red: 'var(--wheel-red)',
  };

  arrowColorMain: Record<WheelColor, string> = {
    gold: 'url(#paint0_linear_88_400)',
    gray: 'var(--wheel-gray)',
    blue: 'var(--wheel-blue)',
    red: 'var(--wheel-red)',
  };

  private intervalId: any;

  constructor() {
    afterRenderEffect(() => {
      if (this.wheelService.winningNumber() !== -1) {
        this.roll(this.wheelService.winningNumber());
      }
    });

    this.destroyRef.onDestroy(() => {
      this.wheelService.winningNumber.set(-1);
      this.wheelService.winningColor.set(null);
      this.wheelService.status.set('rolling');
    });
  }

  ngOnInit(): void {
    this.intervalId = setInterval(() => {
      const rotation = this.getCurrentRotationOfWheel();
      this.currentColor.set(this.findColorByRotation(rotation));
    }, 1000 / 60);
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  async roll(number: number) {
    const rotation = this.generateRotationByNumber(number + 54 * 5);
    await animateElement(
      (p: number) => {
        this.wheelRef().nativeElement.style.transform = `rotate(${
          p * rotation
        }deg)`;
      },
      this.wheelService.wheelSettings.ROLL_DURATION,
      bezier(0, 1, 0.75, 1)
    );
  }

  private findColorByRotation(rotation: number): WheelColor {
    const colorSize = 360 / 54;
    rotation = (rotation + colorSize / 2) % 360;
    const colorNumber = Math.floor(rotation / colorSize);
    return Object.keys(this.wheelService.wheelColors).find((color) =>
      this.wheelService.wheelColors[color as WheelColor].includes(colorNumber)
    )! as WheelColor;
  }

  private generateRotationByNumber(number: number): number {
    const colorSize = 360 / 54;
    const range = {
      min: number * colorSize + 1 - colorSize / 2,
      max: number * colorSize - 1 + colorSize / 2,
    };

    return Math.floor(Math.random() * (range.max - range.min)) + range.min;
  }

  private getCurrentRotationOfWheel() {
    const st = getComputedStyle(this.wheelRef().nativeElement, null);
    const tm =
      st.getPropertyValue('-webkit-transform') ||
      st.getPropertyValue('-moz-transform') ||
      st.getPropertyValue('-ms-transform') ||
      st.getPropertyValue('-o-transform') ||
      st.getPropertyValue('transform') ||
      'none';
    if (tm != 'none') {
      const values: any = tm.split('(')[1].split(')')[0].split(',');

      const angle = Math.round(
        Math.atan2(values[1], values[0]) * (180 / Math.PI)
      );
      return angle < 0 ? angle + 360 : angle;
    }
    return 0;
  }
}
